import clsx from "clsx";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CommandPayload } from "src/data/commands";
import { makeStyles } from "tss-react/mui";

import { deleteBorder } from "@dashboard/shared/styles";
import { DeviceMdmCommandStatusIcon, MdmCommand, MdmCommandOrigin, MdmCommandOriginIcon } from "@devices/types/commands";
import { Box, Collapse, Typography, useTheme } from "@mui/material";
import { grey } from "@mui/material/colors";
import TimeService from "@services/timeService/timeService";
import { DangerOutlinedButton, SecondaryButton } from "@shared/CustomButton";
import { ExpandToggleButton } from "@shared/ExpandToggleButton";
import { MinimalTableDetailsRow } from "@shared/table/CollapsibleRow";
import { MinimalTableCell, MinimalTableRow } from "@shared/table/MinimalTableComponents";
import { Tooltip } from "@shared/Tooltip";

import { getCommandName, getCommandStatusLabel } from "../../shared/CommandTab/CommandTab.utils";

const useStyles = makeStyles()((theme) => ({
    commandPropertyValue: {
        fontSize: "0.9rem",
        display: "inline-block",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    icons: {
        marginTop: theme.spacing(1),
        marginRight: theme.spacing(2),
        fontSize: "0.9rem",
        color: grey[500],
    },
    collapsingRowContent: {
        padding: theme.spacing(2),
        width: "100%",
        minHeight: 36,
        fontSize: "0.9rem",
        textAlign: "left",
    },
    commandPayload: {
        margin: 0,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        overflowWrap: "anywhere",
    },
    collapsePropertyTitle: {
        fontWeight: "bold",
    },
    collapseBtn: {
        "&:focus": {
            outline: "none",
        },
    },
    borderBottomVisible: {
        borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
    },
    overflow: {
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    customCollapse: {
        "& .MuiCollapse-wrapper": {
            maxWidth: "650px",
        },
    },
    deleteButton: {
        marginLeft: theme.spacing(1),
    },
    btnBox: {
        display: "flex",
        justifyContent: "flex-end",
    },
}));

interface DeviceCommandItemProps {
    command: MdmCommand;
    index: number;
    isCreating: boolean;
    isDeleting: boolean;
    createCommand: (json: CommandPayload, commandId: number | null) => Promise<void>;
    deleteCommand: (command: MdmCommand) => Promise<void>;
}

const DeviceCommandItem = ({ command, index, isDeleting, isCreating, deleteCommand, createCommand }: DeviceCommandItemProps) => {
    const { classes } = useStyles();
    const { id, payload, origin: commandOrigin, created, creator, status, updated } = command;
    const [open, setOpen] = useState(-1);

    const theme = useTheme();
    const { t } = useTranslation(["sidepanels"]);

    const getCommandOriginIcon = (origin: string) => {
        if (origin.startsWith(MdmCommandOrigin.DeviceOrigin)) {
            return MdmCommandOriginIcon[MdmCommandOrigin.DeviceOrigin];
        } else if (origin.startsWith(MdmCommandOrigin.GroupOrigin)) {
            return MdmCommandOriginIcon[MdmCommandOrigin.GroupOrigin];
        } else {
            return "fa-solid fa-question";
        }
    };

    const commandJson: CommandPayload = typeof payload === "string" ? JSON.parse(payload) : payload;
    const commandPayload = JSON.stringify(commandJson, null, 2);

    const handleSubmitCommand = async () => {
        await createCommand(commandJson, command.id ?? null);
    };

    const handleDeleteCommand = async () => {
        await deleteCommand(command);
    };

    const createdDate = TimeService.getLocalDate(created, "ISO", "dateTime");
    const lastUpdatedDate = TimeService.getLocalDate(updated, "ISO", "dateTime");

    const originTooltipText = (origin: string): string => {
        if (origin.startsWith("device")) {
            return t("sidepanels:devicePanel.tooltip.createdForDevice");
        } else if (origin.startsWith("group")) {
            return t("sidepanels:devicePanel.tooltip.createdForGroup");
        } else {
            return "";
        }
    };

    const getStatusTooltipText = (): string => {
        if (command.report) {
            return command.report;
        }
        return getCommandStatusLabel(status);
    };

    return (
        <>
            <MinimalTableRow key={id}>
                <MinimalTableCell align="center" style={deleteBorder(index, open)}>
                    <ExpandToggleButton
                        expanded={open === index}
                        onClick={() => setOpen(open === index ? -1 : index)}
                        className={classes.collapseBtn}
                    />
                </MinimalTableCell>
                <MinimalTableCell style={deleteBorder(index, open)}>
                    <Tooltip title={TimeService.getLocalDate(created, "ISO", "dateTime")}>
                        <Typography>{TimeService.getLocalDate(created, "ISO", "date")}</Typography>
                    </Tooltip>
                </MinimalTableCell>
                <MinimalTableCell align="center" style={deleteBorder(index, open)}>
                    <Tooltip placement="top" title={getStatusTooltipText()}>
                        <i className={clsx(classes.icons, DeviceMdmCommandStatusIcon[status] ?? "fa-solid fa-question")} />
                    </Tooltip>
                </MinimalTableCell>
                <MinimalTableCell align="center" style={deleteBorder(index, open)}>
                    <Tooltip placement="top" title={originTooltipText(commandOrigin)}>
                        <i className={clsx(classes.icons, classes.commandPropertyValue, getCommandOriginIcon(commandOrigin))} />
                    </Tooltip>
                </MinimalTableCell>
                <MinimalTableCell align="left" style={deleteBorder(index, open)} className={clsx(classes.overflow)}>
                    <Typography>{getCommandName(commandJson)}</Typography>
                </MinimalTableCell>
                <MinimalTableCell align="right" style={deleteBorder(index, open)}>
                    <Box className={classes.btnBox}>
                        <SecondaryButton
                            tooltipProps={{ title: t("sidepanels:devicePanel.tooltip.resendCommand") }}
                            onClick={handleSubmitCommand}
                            disabled={isCreating}
                            loading={isCreating}
                        >
                            <i className="fa-solid fa-arrows-rotate" />
                        </SecondaryButton>
                        <DangerOutlinedButton
                            style={{ marginLeft: theme.spacing(1) }}
                            tooltipProps={{ title: t("sidepanels:devicePanel.tooltip.deleteCommand") }}
                            onClick={handleDeleteCommand}
                            disabled={command.status !== "pending" || isDeleting}
                            loading={isDeleting}
                        >
                            <i className="fa-solid fa-trash" />
                        </DangerOutlinedButton>
                    </Box>
                </MinimalTableCell>
            </MinimalTableRow>
            <MinimalTableDetailsRow
                colSpan={6}
                className={open === index ? classes.borderBottomVisible : ""}
                cellClassName={classes.customCollapse}
                cellStyle={{ padding: "0 16px" }}
            >
                <Collapse in={open === index} timeout="auto" unmountOnExit>
                    <div className={classes.collapsingRowContent}>
                        <Typography className={classes.collapsePropertyTitle}>
                            {t("sidepanels:devicePanel.commandsTab.details")}
                        </Typography>
                        <Typography>{t("sidepanels:devicePanel.commandsTab.creator", { value: creator })}</Typography>
                        <Typography>{t("sidepanels:devicePanel.commandsTab.created", { value: createdDate })}</Typography>
                        <Typography>{t("sidepanels:devicePanel.commandsTab.updated", { value: lastUpdatedDate })}</Typography>
                        <br />
                        <Typography className={classes.collapsePropertyTitle}>
                            {t("sidepanels:devicePanel.commandsTab.content")}
                        </Typography>
                        <Typography component="pre" className={classes.commandPayload}>
                            {commandPayload}
                        </Typography>
                    </div>
                </Collapse>
            </MinimalTableDetailsRow>
        </>
    );
};

export default DeviceCommandItem;
